import { SERVICES_TAGS } from '../../../constants/servicesTags';
import {
  AgencyInvitesResponse,
  LoginAsUserResponseType,
  UserEventsResponseType,
  userListResType,
} from 'services/user-management/userManagement.api.type';

import {
  ProfileType,
  UserProfileImageType,
  UserProfileType,
  UserType,
  UserUpdateReqType,
} from 'types/core/userTypes';
import { AcceptInvitationEnum, InviteStatusEnum } from 'types/property/propertyEnum';
import { InviteResType, getPropertiesResType } from 'types/property/propertyTypes';
import {
  GoogleLoginRequestProps,
  LoginRequestProps,
  RefreshTokenRequestProps,
  RegisterRequestProps,
  RegisterResponseProps,
} from 'types/user-management/auth';

import initialApiService from '../initialApiService';

const userManagementApiService = initialApiService.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<UserType, LoginRequestProps>({
      query: (body) => ({
        url: '/users/login',
        method: 'POST',
        body,
      }),
    }),
    googleLogin: builder.mutation<UserType, GoogleLoginRequestProps>({
      query: (body) => ({
        url: '/users/login/google',
        method: 'POST',
        body,
      }),
    }),
    googleRegister: builder.mutation<UserType, GoogleLoginRequestProps>({
      query: (body) => ({
        url: '/users/register/google',
        method: 'POST',
        body,
      }),
    }),
    register: builder.mutation<RegisterResponseProps, RegisterRequestProps>({
      query: (body) => ({
        url: '/users/register',
        method: 'POST',
        body,
      }),
    }),
    confirmUser: builder.mutation<ProfileType, { code: string; id: string }>({
      query: (body) => ({
        url: '/users/confirm',
        method: 'PATCH',
        body,
      }),
    }),
    resendConfirmation: builder.mutation<string, { captchaResponse: string; id: string }>({
      query: (body) => ({
        url: '/users/resendconfirmationemail',
        method: 'PATCH',
        body,
      }),
    }),
    refreshToken: builder.mutation<UserType, RefreshTokenRequestProps>({
      query: (body) => ({
        url: '/users/refresh-token',
        method: 'POST',
        body,
      }),
    }),
    getCurrentUser: builder.query<ProfileType, void>({
      query: () => ({
        url: '/users/me',
        method: 'GET',
      }),
      providesTags: [SERVICES_TAGS.UPDATE_PROMOTER],
    }),
    uploadProfilePicture: builder.mutation<ProfileType, UserProfileImageType>({
      query: (body) => ({
        url: '/users/profilepicture',
        method: 'PATCH',
        body,
      }),
    }),
    deleteProfilePicture: builder.mutation<ProfileType, void>({
      query: (body) => ({
        url: 'users/profilepicture',
        method: 'DELETE',
        body,
      }),
    }),
    editProfile: builder.mutation<ProfileType, UserUpdateReqType>({
      query: (body) => ({
        url: '/users/edit',
        method: 'PATCH',
        body,
      }),
    }),
    changePassword: builder.mutation<
      ProfileType,
      { oldPassword: string; newPassword: string; confirmPassword: string }
    >({
      query: (body) => ({
        url: '/users/changepassword',
        method: 'PATCH',
        body,
      }),
    }),
    searchUser: builder.query<UserProfileType[], { term: string }>({
      query: (params) => ({
        url: '/users/search',
        method: 'GET',
        params,
      }),
    }),
    getUserEvents: builder.query<
      UserEventsResponseType,
      { IncludeMetrics?: boolean; PageSize?: number; PageNumber?: number; Search?: string }
    >({
      query: (params) => ({
        url: '/users/events',
        method: 'GET',
        params,
      }),
      providesTags: [SERVICES_TAGS.GET_USERS_EVENT],
    }),
    getInviteLists: builder.query<
      AgencyInvitesResponse,
      { status: InviteStatusEnum; PageSize?: number; PageNumber?: number }
    >({
      query: (params) => ({
        url: '/users/invites',
        method: 'GET',
        params,
      }),
    }),
    processInvite: builder.mutation<InviteResType, { status: AcceptInvitationEnum; id: string }>({
      query: (body) => ({
        url: '/users/acceptrejectinvite',
        method: 'POST',
        body,
      }),
    }),
    userProperties: builder.mutation<
      getPropertiesResType,
      { pageNumber: number; pageSize: number }
    >({
      query: (body) => ({
        url: '/users/properties',
        method: 'POST',
        body,
      }),
    }),
    getUserList: builder.query<UserType[], string>({
      query: (params) => ({
        url: `/admin/searchusers?term=${params}`,
        method: 'GET',
      }),
    }),
    forgotPassword: builder.mutation<
      string,
      {
        email: string;
        captchaResponse: string;
      }
    >({
      query: (body) => ({
        url: '/users/forgotpassword',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<
      ProfileType,
      {
        newPassword: string;
        confirmPassword: string;
        token: string;
      }
    >({
      query: (body) => ({
        url: '/users/resetpassword',
        method: 'POST',
        body,
      }),
    }),
    loginAsUser: builder.mutation<LoginAsUserResponseType, { email: string }>({
      query: (body) => ({
        url: '/admin/loginas',
        method: 'POST',
        body,
      }),
    }),
    listUser: builder.mutation<
      userListResType,
      { pageNumber: number; pageSize: number; term?: string }
    >({
      query: (body) => ({
        url: '/admin/users',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const loginEndpoint = userManagementApiService.endpoints.login;
export const googleLoginEndpoint = userManagementApiService.endpoints.googleLogin;
export const googleRegisterEndpoint = userManagementApiService.endpoints.googleRegister;

export const {
  useLoginMutation,
  useGoogleLoginMutation,
  useGoogleRegisterMutation,
  useConfirmUserMutation,
  useResendConfirmationMutation,
  useListUserMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useUserPropertiesMutation,
  useDeleteProfilePictureMutation,
  useRegisterMutation,
  useLazyGetInviteListsQuery,
  useProcessInviteMutation,
  useLazyGetCurrentUserQuery,
  useGetCurrentUserQuery,
  useUploadProfilePictureMutation,
  useEditProfileMutation,
  useChangePasswordMutation,
  useLazySearchUserQuery,
  useLazyGetUserEventsQuery,
  useLazyGetUserListQuery,
  useLoginAsUserMutation,
} = userManagementApiService;
